/* eslint-disable react/prop-types */
import style from "./style.module.scss";

import React, { Fragment, ReactElement, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Select } from "@jmc/solid-design-system/src/components/atoms/Select/Select";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import classnames from "classnames";
import cleanCSSIdentifier from "@jmc/utils/utils/clean-css-identifier";
import { BreakPoint, useMediaQuery } from "../../../hooks/useMediaQuery";
import debounce from "@jmc/utils/utils/debounce";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { useScreenSize } from "@jmc/utils/hooks/useScreenSize";
import { Icon, jnjChevronLeft, jnjChevronRight } from "@jmc/solid-design-system/src/components/atoms/Icon/Icon";
import { getLanguage } from "@jmc/solid-design-system/src/utils/languages";

type TabProps = {
    title: Element | string | JSX.Element;
    rawTitle?: string;
    children: ReactElement | ReactElement[];
    autoId?: string;
};

export interface PropTypes {
    children: ReactNode | ReactNode[];
    selectedTab?: number;
    onChange?: (index: number, event?: any) => void;
    mobileBreakpoint?: BreakPoint;
    maxVisibleTabs?: number;
    locale?: string;
    sectionBackground?: string;
    scrollLeftLiteral?: string;
    scrollRightLiteral?: string;
    noSpacing?: boolean;
    noTopSpacing?: boolean;
}

const Tab = (props: TabProps) => {
    return <Fragment {...props}></Fragment>;
};

const SCROLL_THROTTLE = 50;

export const Tabs = ({
    children,
    selectedTab,
    onChange,
    mobileBreakpoint = 576,
    maxVisibleTabs,
    locale,
    sectionBackground,
    scrollLeftLiteral = "Scroll left",
    scrollRightLiteral = "Scroll right",
    noSpacing = false,
    noTopSpacing = false,
}: PropTypes) => {
    const [activeTab, setActiveTab] = React.useState<number>(selectedTab || 0);
    const { jnjFullBranded } = useJnjBranding();
    const languageDirection = locale && getLanguage(locale?.split("-").shift())?.dir;

    const [scrollLeft, setScrollLeft] = useState(false);
    const [scrollRight, setScrollRight] = useState(false);
    const [scrollShowing, setScrollShowing] = useState(false);
    const [current, setCurrent] = useState(0);

    const useHookWithRefCallback = (): [tabsRef: any, setTabsRef: (node: any) => void] => {
        //https://medium.com/@teh_builder/ref-objects-inside-useeffect-hooks-eb7c15198780
        const tabsRef = useRef(null);
        const setTabsRef = useCallback((node: HTMLDivElement) => {
            if (node && jnjFullBranded) {
                const scrollWidth = node?.scrollWidth;
                const clientWidth = node?.clientWidth;
                setScrollShowing(scrollWidth > clientWidth);
            }
            tabsRef.current = node;
        }, []);

        return [tabsRef, setTabsRef];
    };

    const [tabsRef, setTabsRef] = useHookWithRefCallback();
    const screenSize = useScreenSize();

    useEffect(() => {
        if (tabsRef && jnjFullBranded) {
            const scrollWidth = tabsRef?.current?.scrollWidth;
            const clientWidth = tabsRef?.current?.clientWidth;
            setScrollShowing(scrollWidth > clientWidth);
        }
    }, [children, screenSize?.width]);

    useEffect(() => {
        if (jnjFullBranded) {
            setCurrent(0);
            if (languageDirection) {
                setScrollLeft(false);
                setScrollRight(scrollShowing);
            } else {
                setScrollRight(false);
                setScrollLeft(scrollShowing);
            }
        }
    }, [scrollShowing]);

    const handleKeyPress = (event: React.KeyboardEvent<HTMLAnchorElement>, index: number): void => {
        if (event.key === "Enter") {
            setActiveTab(index);
            onChange && onChange(index, event);
        }
    };

    const scrollStartToEnd = (items: HTMLDListElement[]) => {
        const newIndex = Math.max(current - 1, 0);
        const elem = items[newIndex];

        const scrollToLeft = languageDirection
            ? Math.max(elem?.offsetLeft - 24, 0)
            : elem?.offsetLeft + elem?.clientWidth - tabsRef?.current?.clientWidth + 24;

        tabsRef.current.scrollTo({
            left: scrollToLeft,
            behavior: "smooth",
        });
    };

    const scrollEndToStartLtr = (items: HTMLDListElement[]) => {
        const newIndex = Math.min(current + 1, items.length - 1);
        const elem = items[newIndex];

        tabsRef.current.scrollTo({
            left: Math.max(elem?.offsetLeft - 24, 0),
            behavior: "smooth",
        });
    };

    const scrollEndToStartRtl = (items: HTMLDListElement[]) => {
        const nextElement = items[current];

        tabsRef.current.scrollTo({
            left: nextElement?.offsetLeft - tabsRef?.current?.clientWidth + 24,
            behavior: "smooth",
        });
    };

    const selectedTabIntoView = (index: number) => {
        const targetTab = tabsRef.current?.children?.[index];

        const isTabFullInViewLtr =
            Math.max(targetTab?.offsetLeft - 24, 0) >= tabsRef?.current?.scrollLeft &&
            Math.min(targetTab?.offsetLeft + targetTab?.clientWidth + 24, tabsRef?.current?.scrollWidth) <=
                tabsRef?.current?.scrollLeft + tabsRef?.current?.clientWidth;

        const isTabFullInViewRtl =
            Math.max(targetTab?.offsetLeft - 24, tabsRef?.current?.clientWidth - tabsRef?.current?.scrollWidth) >=
                tabsRef?.current?.scrollLeft &&
            Math.min(targetTab?.offsetLeft + targetTab?.clientWidth + 24, tabsRef?.current?.clientWidth) <=
                tabsRef?.current?.scrollLeft + tabsRef?.current?.clientWidth;

        // Only scrolls a tab into view if the tab is not fully visible
        if (languageDirection ? !isTabFullInViewLtr : !isTabFullInViewRtl) {
            const offset = languageDirection
                ? Math.max(targetTab?.offsetLeft - 24, 0)
                : targetTab?.offsetLeft + targetTab?.clientWidth - tabsRef?.current?.clientWidth + 24;
            tabsRef?.current?.scrollTo({
                left: offset,
                behavior: "smooth",
            });
        }
    };

    React.useEffect(() => {
        if (selectedTab || selectedTab == 0) {
            setActiveTab(selectedTab);
            if (selectedTab !== current) {
                selectedTabIntoView(selectedTab);
            }
        }
    }, [selectedTab]);

    const handleArrowClick = (arrow: "left" | "right") => {
        if (jnjFullBranded) {
            const items = tabsRef.current.querySelectorAll(`button[role="tab"]`);
            if (languageDirection) {
                // Left-to-Right
                arrow === "left" ? scrollStartToEnd(items) : scrollEndToStartLtr(items);
            } else {
                // Right-to-Left
                arrow === "left" ? scrollEndToStartRtl(items) : scrollStartToEnd(items);
            }
        }
    };

    const showSelect =
        !jnjFullBranded &&
        (useMediaQuery(mobileBreakpoint) || (maxVisibleTabs && maxVisibleTabs < React.Children.count(children)));

    const tabs = React.Children.toArray(children);

    const getCurrentIndex = (items, scrolled) => {
        let index = 0;
        let passed = 0;
        items?.forEach((tab, i) => {
            passed = passed + tab?.clientWidth;
            if (languageDirection ? scrolled + 1 >= passed - 24 : scrolled - 1 <= 24 - passed) index = i + 1;
        });
        return index;
    };

    const tabContainer = tabsRef?.current;

    const onTabScroll = () => {
        const scrolled = tabsRef?.current?.scrollLeft;
        const scrollWidth = tabsRef?.current?.scrollWidth;
        const tabWidth = tabsRef?.current?.clientWidth;
        const items = tabsRef?.current?.querySelectorAll(`button[role="tab"]`);
        const currentIndex = getCurrentIndex(items, scrolled);
        setCurrent(currentIndex);

        // Show and hide arrow buttons based on position
        if (languageDirection) {
            scrolled > 0 ? !scrollLeft && setScrollLeft(true) : scrollLeft && setScrollLeft(false);
            tabWidth + scrolled + 1 >= scrollWidth
                ? scrollRight && setScrollRight(false)
                : !scrollRight && setScrollRight(true);
        } else {
            scrolled < 0 ? !scrollRight && setScrollRight(true) : scrollRight && setScrollRight(false);
            tabWidth - scrolled + 1 >= scrollWidth
                ? scrollLeft && setScrollLeft(false)
                : !scrollLeft && setScrollLeft(true);
        }
    };

    const debouncedCheckScroll = debounce(onTabScroll, SCROLL_THROTTLE);

    useEffect(() => {
        onTabScroll();
        tabContainer?.addEventListener("scroll", debouncedCheckScroll, true);

        return (): void => {
            tabContainer?.removeEventListener("scroll", debouncedCheckScroll, true);
        };
    });

    const tabsWrapper = (
        <ul className={style.tabs} role="tablist" ref={setTabsRef}>
            {React.Children.map(children, ({ props }: { props: TabProps }, index: number) => {
                const isTabActive = activeTab === index;
                return (
                    <button
                        aria-controls={`tabpane.${cleanCSSIdentifier(props?.rawTitle?.toLowerCase())}`}
                        aria-selected={isTabActive}
                        role="tab"
                        className={classnames(style.tab, isTabActive ? style.active : null)}
                        key={index}
                        onClick={(e: React.MouseEvent) => {
                            setActiveTab(index);
                            onChange && onChange(index, e);
                        }}
                        onKeyUp={(e) => handleKeyPress(e, index)}
                        data-test-id={`Tabs.Tab.${index}`}
                        data-tracking-name={props?.rawTitle}
                        data-tracking-index={index}
                        data-role={"Tabs.Tab"}
                        id={props?.autoId ? props?.autoId : cleanCSSIdentifier(`Tab.${props?.rawTitle?.toLowerCase()}`)}
                    >
                        <Typography
                            component="span"
                            color="inherit"
                            weight={jnjFullBranded ? "400" : "600"}
                            variant={jnjFullBranded ? "label-01" : "body"}
                        >
                            {props?.title}
                        </Typography>
                    </button>
                );
            })}
        </ul>
    );

    return (
        <div
            data-test-id="Tabs"
            className={classnames(style.tabsElement, jnjFullBranded && (noSpacing ? style.noSpacing : null))}
        >
            {showSelect ? (
                <Select
                    onChange={(value): void => {
                        if (!Array.isArray(value)) {
                            setActiveTab(value.value as number);
                            onChange && onChange(value.value as number, { currentTarget: null });
                        }
                    }}
                    isSearchable={false}
                >
                    {React.Children.map(children, ({ props }: { props: TabProps }, index: number) => (
                        <Select.Option key={index} value={index} selected={activeTab === index}>
                            <div
                                data-role={"Tabs.Tab"}
                                data-tracking-name={props?.rawTitle}
                                data-tracking-index={index}
                                id={props?.autoId}
                            >
                                {props?.title}
                            </div>
                        </Select.Option>
                    ))}
                </Select>
            ) : jnjFullBranded ? (
                <div
                    className={classnames(
                        style.container,
                        scrollLeft ? style.scrollLeft : null,
                        scrollRight ? style.scrollRight : null,
                        sectionBackground?.toLowerCase() === "white" ? style.white : null,
                        noSpacing ? style.noSpacing : null,
                        noTopSpacing ? style.noTopSpacing : null,
                    )}
                >
                    <div
                        tabIndex={0}
                        className={classnames(style.scroll, style.scrollLeft, scrollLeft ? style.visible : null)}
                        onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>): void =>
                            keyHandler(e.key, () => handleArrowClick("left"))
                        }
                        onClick={() => handleArrowClick("left")}
                        aria-label={scrollLeftLiteral}
                        data-test-id="MegaMenu.LeftArrow"
                        role="button"
                    >
                        <Icon color="black" icon={jnjChevronLeft} type="jnj" size="xs" />
                    </div>
                    {tabsWrapper}
                    <div
                        tabIndex={0}
                        className={classnames(style.scroll, style.scrollRight, scrollRight ? style.visible : null)}
                        onClick={() => handleArrowClick("right")}
                        aria-label={scrollRightLiteral}
                        data-test-id="MegaMenu.RightArrow"
                        onKeyDown={(e: React.KeyboardEvent<HTMLAnchorElement>): void =>
                            keyHandler(e.key, () => handleArrowClick("right"))
                        }
                        role="button"
                    >
                        <Icon color="black" icon={jnjChevronRight} type="jnj" size="xs" />
                    </div>
                </div>
            ) : (
                tabsWrapper
            )}
            {tabs.map((tab, index) => (
                <span
                    key={`tabkey${index}-${activeTab}`}
                    className={classnames(
                        jnjFullBranded ? style.tabContent : null,
                        jnjFullBranded && (noSpacing ? style.noSpacing : style.spacing),
                        index !== activeTab ? style.hidden : null,
                    )}
                >
                    {/* @ts-expect-error because */}
                    {(tab.props as TabProps)?.children}
                </span>
            ))}
        </div>
    );
};

Tabs.Tab = Tab;

export default Tabs;
